import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import PendingUserApproval from "../models/PendingUserApproval";
import { WorkLogWeekly } from "../models/WorkLogWeekly";
import WorkLogService from "../services/WorkLogService";
import WorkLogWeeklyService from "../services/WorkLogWeeklyService";
import { QueryKeyFactory, WORKLOG_DAILY_LIST_CONTEXT } from "../services/queryKeys";
import OpsDateUtils from "../utils/OpsDateUtils";
import { unwrap } from "../utils/queryFn";
import { worklogApprovalKey } from "../utils/WorklogApprovalUtils";

const weeklyKeys = new QueryKeyFactory(WorkLogWeeklyService.path);
const workLogKeys = new QueryKeyFactory(WorkLogService.path);
const PENDING_CONTEXT = { scope: "pending-approvals" };
/** Pending-approvals list the dashboard approval tile reads and quick-log writes invalidate it. */
export const pendingKey = weeklyKeys.lists(PENDING_CONTEXT);
const NO_PENDING: PendingUserApproval[] = [];

const weekTime = (user: PendingUserApproval): number => OpsDateUtils.parseIsoDate(user.weekFirstDayIso).getTime();

const sortOldestFirst = (rows: PendingUserApproval[]): PendingUserApproval[] =>
  [...rows].sort((a, b) => {
    const byWeek = weekTime(a) - weekTime(b);
    if (byWeek !== 0) return byWeek;
    return (a.userFullName || "").localeCompare(b.userFullName || "");
  });

const toWeekPayload = (user: PendingUserApproval, validated: boolean): WorkLogWeekly => ({
  assignationExternalId: user.assignationExternalId,
  shortWeekFirstDay: OpsDateUtils.toUTC(OpsDateUtils.parseIsoDate(user.weekFirstDayIso)),
  validated,
});

type WeekDecision = {
  user: PendingUserApproval;
  validated: boolean;
};

export const useWorklogsApproval = (onApprovalChange?: () => void) => {
  const queryClient = useQueryClient();

  const pendingQuery = useQuery({
    queryKey: pendingKey,
    queryFn: async () => {
      const rows = await unwrap(WorkLogWeeklyService.getPendingUserApprovals());
      return sortOldestFirst(rows || []);
    },
  });

  const refreshApprovalViews = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: pendingKey }),
      queryClient.invalidateQueries({ queryKey: workLogKeys.lists(WORKLOG_DAILY_LIST_CONTEXT) }),
    ]);
  };

  const approveWeekMutation = useMutation({
    mutationFn: (decision: WeekDecision) => unwrap(WorkLogWeeklyService.approveWeek(toWeekPayload(decision.user, decision.validated))),
    onSuccess: async (_saved, decision) => {
      const uniqueId = worklogApprovalKey(decision.user.assignationExternalId, decision.user.weekFirstDayIso);
      if (decision.validated) {
        queryClient.setQueryData<PendingUserApproval[]>(pendingKey, (current) =>
          (current ?? NO_PENDING).filter((item) => worklogApprovalKey(item.assignationExternalId, item.weekFirstDayIso) !== uniqueId),
        );
        NotificationService.info(`Approved worklog for ${decision.user.userFullName}`);
      } else {
        NotificationService.info(`Sent back worklog for ${decision.user.userFullName}`);
      }
      await refreshApprovalViews();
      onApprovalChange?.();
    },
  });

  const approveAllMutation = useMutation({
    mutationFn: (rows: PendingUserApproval[]) => unwrap(WorkLogWeeklyService.approveItems(rows.map((user) => toWeekPayload(user, true)))),
    onSuccess: async (batchResponse, rows) => {
      const failed = batchResponse.totalErrors || 0;
      const approved = rows.length - failed;
      if (failed > 0) {
        NotificationService.warn(`Approved ${approved} worklog${approved === 1 ? "" : "s"}, ${failed} failed. Open the queue for details.`);
      } else {
        NotificationService.info(`Approved ${approved} worklog${approved === 1 ? "" : "s"}`);
      }
      await refreshApprovalViews();
      onApprovalChange?.();
    },
  });

  const pendingUsers = pendingQuery.data ?? NO_PENDING;
  const busy = approveWeekMutation.isPending || approveAllMutation.isPending;
  const processingId =
    approveWeekMutation.isPending && approveWeekMutation.variables
      ? worklogApprovalKey(approveWeekMutation.variables.user.assignationExternalId, approveWeekMutation.variables.user.weekFirstDayIso)
      : undefined;

  const decideWeek = (user: PendingUserApproval, validated: boolean) => {
    if (busy) return;
    approveWeekMutation.mutate({ user, validated });
  };

  const handleApproveAll = () => {
    if (busy || pendingUsers.length === 0) return;
    approveAllMutation.mutate(pendingUsers);
  };

  return {
    pendingUsers,
    isLoading: pendingQuery.isLoading,
    isApprovingAll: approveAllMutation.isPending,
    processingId,
    handleApprove: (user: PendingUserApproval) => decideWeek(user, true),
    handleReject: (user: PendingUserApproval) => decideWeek(user, false),
    handleApproveAll,
  };
};
